/**
 * ChatLogRow — a chat message rendered as a game-log line, with the same
 * long-press actions as a chat bubble (MessageActions): quick reactions, the
 * full emoji picker, copy, and flag (others' messages only).
 *
 * A tap does NOT navigate anywhere — the log is the conversation. Reactions
 * show as small chips just left of the time-ago.
 *
 * A NEW line (lib/freshChat) fades into its spot on a tinted background that
 * fades back to a normal row (this replaced the map-bar ticker).
 *
 * Handlers come from ChatRowActionsProvider (the game screen);
 * without one the row renders read-only.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Animated, Easing, Pressable, View } from 'react-native'
import { useReducedMotion } from 'react-native-reanimated'
import { useRouter } from 'expo-router'
import type { ChatReactionEmoji, GamePlayerResponse } from '@warcarp/api-types'
import { useToggleChatReaction } from '../../hooks/useChat'
import type { FreshChatRegistry } from '../../hooks/useFreshChatHighlights'
import { CHAT_ENTRANCE_MS, CHAT_HIGHLIGHT_FADE_MS, CHAT_HIGHLIGHT_HOLD_MS } from '../../lib/freshChat'
import type { LogRow } from '../../lib/logFeed'
import { visibleChatMessageIds } from '../../lib/chatSeen'
import { useToast } from '../ui/Toast'
import { EventLogRow } from './EventLogRow'
import { EmojiPickerModal, MessageActionTray, ReactionChips, reactionsA11y, useMessageActions } from './MessageActions'

interface ChatRowActions {
  currentUserId?: string
  onReact: (messageId: string, emoji: ChatReactionEmoji) => void
  onReport: (message: { messageId: string; authorId: string }) => void
  reactingMessageId?: string
  /** New-message highlight registry (game screen only). */
  fresh?: FreshChatRegistry
  /** The chat lines a log list has on screen, per list (game screen only):
   *  they count as read (lib/chatSeen). An empty list clears it. */
  onChatRowsSeen?: (listId: string, messageIds: string[]) => void
}

const ChatRowActionsContext = createContext<ChatRowActions | null>(null)

/** Supplies reaction/report handlers to every ChatLogRow beneath it. */
export function ChatRowActionsProvider({
  gameId,
  currentUserId,
  fresh,
  onChatRowsSeen,
  children,
}: {
  gameId: string
  currentUserId?: string
  fresh?: FreshChatRegistry
  onChatRowsSeen?: (listId: string, messageIds: string[]) => void
  children: React.ReactNode
}) {
  const router = useRouter()
  const { showToast } = useToast()
  const toggleReaction = useToggleChatReaction(gameId)
  const onReact = useCallback((messageId: string, emoji: ChatReactionEmoji) => {
    toggleReaction.mutate(
      { messageId, emoji },
      { onError: () => showToast("Couldn't update reaction — please try again.", 'error') },
    )
  }, [showToast, toggleReaction])
  const onReport = useCallback(({ messageId, authorId }: { messageId: string; authorId: string }) => {
    router.push({ pathname: '/(app)/settings/blocked-users', params: { block: authorId, messageId, kind: 'chat' } })
  }, [router])
  const reactingMessageId = toggleReaction.isPending ? toggleReaction.variables?.messageId : undefined
  const value = useMemo(
    () => ({ currentUserId, onReact, onReport, reactingMessageId, fresh, onChatRowsSeen }),
    [currentUserId, onReact, onReport, reactingMessageId, fresh, onChatRowsSeen],
  )
  return <ChatRowActionsContext.Provider value={value}>{children}</ChatRowActionsContext.Provider>
}

/** Half a line on screen counts as seen. Module scope: FlatList needs it stable. */
const CHAT_SEEN_VIEWABILITY = { itemVisiblePercentThreshold: 50 }

/**
 * FlatList props that report a log list's on-screen chat lines to the
 * provider (read tracking, lib/chatSeen); nothing happens where no provider
 * tracks it. The list clears its entry when it unmounts.
 */
export function useChatSeenViewability(listId: string) {
  const report = useContext(ChatRowActionsContext)?.onChatRowsSeen
  const reportRef = useRef(report)
  reportRef.current = report
  // FlatList rejects a changing onViewableItemsChanged: keep one per mount.
  const onViewableItemsChanged = useCallback(
    ({ viewableItems }: { viewableItems: { isViewable: boolean; item: unknown }[] }) => {
      reportRef.current?.(listId, visibleChatMessageIds(viewableItems))
    },
    [listId],
  )
  useEffect(() => () => reportRef.current?.(listId, []), [listId])
  // Always the same handler: a no-op where nothing tracks it.
  return { onViewableItemsChanged, viewabilityConfig: CHAT_SEEN_VIEWABILITY }
}

export function ChatLogRow({
  row,
  playerBySlot,
  variant = 'compact',
}: {
  row: LogRow
  playerBySlot: Map<string, GamePlayerResponse>
  variant?: 'full' | 'compact'
}) {
  const chat = row.chat!
  const ctx = useContext(ChatRowActionsContext)
  const [chipHover, setChipHover] = useState(false)
  const isOwn = !!ctx?.currentUserId && chat.authorId === ctx.currentUserId
  const reactions = chat.reactions ?? []
  const reactionA11y = reactionsA11y(reactions, ctx?.currentUserId)
  const actions = useMessageActions({
    messageId: chat.messageId,
    content: chat.content,
    alignEnd: true,
    onReact: ctx?.onReact,
  })
  const isReacting = ctx?.reactingMessageId === chat.messageId

  // New-message entrance: a fresh line (lib/freshChat) fades into its spot on
  // a blood-tinted background that holds 5 s then fades to a normal row. Plays once: on first render, or when the line
  // turns fresh while on screen.
  const fresh = ctx?.fresh
  const reducedMotion = useReducedMotion()
  const freshAtMount = useRef(!!fresh?.isFresh(chat.messageId)).current
  const enter = useRef(new Animated.Value(freshAtMount && !reducedMotion ? 0 : 1)).current
  const highlight = useRef(new Animated.Value(0)).current
  const entranceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => { if (entranceTimer.current) clearTimeout(entranceTimer.current) }, [])
  const [highlighting, setHighlighting] = useState(false)
  // Once the entrance has had its time, the row switches to a plain opacity
  // of 1 instead of trusting the animated value to land there. A view can
  // stop following its animated opacity (React's dev double-mount detaches
  // it), and a row that never plays the entrance (another copy of the same
  // message played it) has no animation coming — either way the line stayed
  // invisible.
  const [entranceDone, setEntranceDone] = useState(!freshAtMount || reducedMotion)
  useEffect(() => {
    if (!fresh?.isFresh(chat.messageId)) {
      if (!entranceTimer.current) setEntranceDone(true)
      return
    }
    fresh.markPlayed(chat.messageId)
    setEntranceDone(false)
    if (entranceTimer.current) clearTimeout(entranceTimer.current)
    entranceTimer.current = setTimeout(() => setEntranceDone(true), reducedMotion ? 0 : CHAT_ENTRANCE_MS)
    setHighlighting(true)
    highlight.setValue(1)
    const settle = Animated.sequence([
      Animated.delay(CHAT_HIGHLIGHT_HOLD_MS),
      Animated.timing(highlight, { toValue: 0, duration: CHAT_HIGHLIGHT_FADE_MS, useNativeDriver: true }),
    ])
    if (reducedMotion) {
      enter.setValue(1)
    } else {
      enter.setValue(0)
      Animated.timing(enter, {
        toValue: 1,
        duration: CHAT_ENTRANCE_MS,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }).start()
    }
    settle.start(({ finished }) => { if (finished) setHighlighting(false) })
  }, [fresh, chat.messageId, highlight, enter, reducedMotion])

  return (
    <Animated.View
      style={{
        position: 'relative',
        zIndex: actions.trayOpen || chipHover ? 100 : 0,
        // Fades into its spot (no movement).
        opacity: entranceDone ? 1 : enter,
      }}
    >
      {highlighting && (
        // The tint is on a plain inner View: a className on an Animated.View
        // isn't applied on web, so the new-message flash never showed there.
        <Animated.View
          pointerEvents="none"
          testID={`chat-log-row-highlight-${chat.messageId}`}
          style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, opacity: highlight }}
        >
          <View className="bg-blood/15" style={{ flex: 1 }} />
        </Animated.View>
      )}
      <Pressable
        disabled={!ctx}
        onPress={actions.handlePress}
        onLongPress={actions.handleLongPress}
        onPressOut={actions.handlePressOut}
        delayLongPress={350}
        // The row is one accessibility element, so iOS hides the reaction
        // chips inside it: speak them in the label and offer each toggle as an
        // action instead.
        accessibilityLabel={`Chat from ${chat.authorName}: ${chat.content}${reactionA11y.summary ? `. ${reactionA11y.summary}` : ''}`}
        accessibilityHint="Long press for reactions, copy and flag"
        accessibilityActions={ctx ? reactionA11y.actions.map(({ name, label }) => ({ name, label })) : undefined}
        onAccessibilityAction={(event) => {
          const action = reactionA11y.actions.find(({ name }) => name === event.nativeEvent.actionName)
          if (action) actions.toggleReaction(action.emoji)
        }}
        testID={`chat-log-row-${chat.messageId}`}
      >
        {/* Reaction chips sit just left of the time-ago, on the same line. */}
        <EventLogRow
          variant={variant}
          row={row}
          playerBySlot={playerBySlot}
          reactionCounts={Array.from(new Set(reactions.map(reaction => reaction.emoji))).map(emoji => reactions.filter(reaction => reaction.emoji === emoji).length)}
          trailing={reactions.length > 0 ? (
            <ReactionChips
          reactions={reactions}
          currentUserId={ctx?.currentUserId}
          isReacting={isReacting}
          canReact={!!ctx}
          alignEnd
          size="small"
          onToggleReaction={actions.toggleReaction}
          onHoverChange={setChipHover}
        />
          ) : undefined}
        />
      </Pressable>

      {/* Tray sits INSIDE the row's right side (not above it), so the first
          row of a scrolled list can't clip it. */}
      {ctx && actions.trayOpen && (
        <View style={{ position: 'absolute', right: 6, top: 2, zIndex: 20 }}>
          <MessageActionTray
            trayRef={actions.trayRef}
            reactions={reactions}
            currentUserId={ctx.currentUserId}
            isReacting={isReacting}
            canReact
            copied={actions.copied}
            onToggleReaction={actions.toggleReaction}
            onOpenPicker={actions.openPicker}
            onCopy={() => { void actions.copy() }}
            onReport={!isOwn
              ? () => { actions.close(); ctx.onReport({ messageId: chat.messageId, authorId: chat.authorId }) }
              : undefined}
          />
        </View>
      )}


      {ctx && actions.pickerOpen && (
        <EmojiPickerModal
          position={actions.pickerPosition}
          reactions={reactions}
          currentUserId={ctx.currentUserId}
          isReacting={isReacting}
          onToggleReaction={actions.toggleReaction}
          onClose={actions.close}
        />
      )}
    </Animated.View>
  )
}
